import {
  CheckCircleOutline as ActionableIcon,
  ExpandLess,
  ExpandMore,
  RemoveCircleOutline as DismissIcon,
  Visibility as WatchIcon,
  TrendingUp as DevelopingIcon,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";

import type { AngleStatus, OpportunityAngle } from "../../../types/accountIntelligence";

const ANGLE_STATUS_CONFIG: Record<AngleStatus, { label: string; color: string }> = {
  actionable: { label: "Actionable", color: "#2e7d32" },
  developing: { label: "Developing", color: "#e65100" },
  watch: { label: "Watch", color: "#1976d2" },
  dormant: { label: "Dormant", color: "#616161" },
  dismissed: { label: "Dismissed", color: "#424242" },
};

const SIGNAL_TYPE_LABEL: Record<string, string> = {
  hiring: "Hiring",
  investment: "Investment",
  partnership: "Partnership",
  expansion: "Expansion",
  contract: "Contract",
  event: "Event",
  product_launch: "Product Launch",
  regulatory: "Regulatory",
  ma: "M&A",
};

interface OpportunityAngleCardProps {
  angle: OpportunityAngle;
  defaultExpanded?: boolean;
  onDismiss?: (id: string) => void;
  onPromote?: (id: string) => void;
}

const OpportunityAngleCard: React.FC<OpportunityAngleCardProps> = ({
  angle,
  defaultExpanded = false,
  onDismiss,
  onPromote,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const cfg = ANGLE_STATUS_CONFIG[angle.status];

  const StatusIcon =
    angle.status === "actionable"
      ? ActionableIcon
      : angle.status === "developing"
      ? DevelopingIcon
      : WatchIcon;

  return (
    <Paper
      elevation={0}
      sx={{
        borderLeft: `3px solid ${cfg.color}`,
        backgroundColor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderLeftColor: cfg.color,
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.5,
          cursor: "pointer",
        }}
        onClick={() => setExpanded((e) => !e)}
      >
        <StatusIcon sx={{ color: cfg.color, fontSize: 18, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {angle.title}
          </Typography>
        </Box>
        <Chip
          label={cfg.label}
          size="small"
          sx={{
            color: cfg.color,
            backgroundColor: `${cfg.color}18`,
            border: `1px solid ${cfg.color}40`,
            fontSize: "0.68rem",
            fontWeight: 600,
            flexShrink: 0,
          }}
        />
        <IconButton size="small" sx={{ flexShrink: 0, color: "text.secondary" }}>
          {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ px: 2, py: 1.5 }}>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.25}>
                WHY IT MATTERS
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {angle.rationale}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Box sx={{ flex: 1, minWidth: 160 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.25}>
                  FIT BASIS
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {angle.fitBasis}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 160 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.25}>
                  TIMING BASIS
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {angle.timingBasis}
                </Typography>
              </Box>
            </Box>

            {angle.supportingSignals.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                  SUPPORTING SIGNALS
                </Typography>
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                  {angle.supportingSignals.map((sig) => (
                    <Chip
                      key={sig.id}
                      label={sig.title}
                      size="small"
                      sx={{ fontSize: "0.68rem", backgroundColor: "rgba(255,255,255,0.05)" }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Box
              sx={{
                backgroundColor: "rgba(25,118,210,0.08)",
                border: "1px solid rgba(25,118,210,0.2)",
                borderRadius: 1,
                px: 1.5,
                py: 1,
              }}
            >
              <Typography variant="caption" color="primary.main" fontWeight={600} display="block" mb={0.25}>
                RECOMMENDED NEXT STEP
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {angle.nextStep}
              </Typography>
            </Box>

            {(onDismiss || onPromote) && (
              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                {onDismiss && (
                  <Tooltip title="Dismiss this angle">
                    <Chip
                      icon={<DismissIcon sx={{ fontSize: 14 }} />}
                      label="Dismiss"
                      size="small"
                      onClick={() => onDismiss(angle.id)}
                      sx={{ cursor: "pointer", color: "text.secondary" }}
                    />
                  </Tooltip>
                )}
                {onPromote && angle.status !== "actionable" && (
                  <Tooltip title="Mark as Actionable">
                    <Chip
                      icon={<ActionableIcon sx={{ fontSize: 14 }} />}
                      label="Mark Actionable"
                      size="small"
                      onClick={() => onPromote(angle.id)}
                      sx={{
                        cursor: "pointer",
                        color: "#2e7d32",
                        backgroundColor: "rgba(46,125,50,0.1)",
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            )}
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
};

export { SIGNAL_TYPE_LABEL };
export default OpportunityAngleCard;
